import _ from 'lodash';
import {openDB, deleteDB, wrap, unwrap} from 'idb';
import stringHash from 'string-hash';
import qs from 'query-string';
import syncConfig from '../offline/sync.config.js';
import {StatementTypeIds} from '../util/uiconstants';
import {documentGetAllParamNames} from '../util/uiconstants.js';

/*
 * Injection of config into the middleware to use it for authentication
 * config can be passed as a parameter to the middleware in applyMiddleware method when creating the store
 */
const idb_middleware = (config) => (store) => (next) => (action) => {
	/* Existence of the meta.api object is the trigger to treat this
	 * action as an API call. */

	if (action.type === 'UPDATE_APP_ONLINE_STATE' || (action.meta && action.meta.docHelper)) {
		next(action);
		return;
	}

	if (!action.meta || !(action.meta.api || action.meta.worker)) {
		next(action);
		return;
	}

	let {method, url, data, headers} = action.meta.api || action.meta,
		auth = action.meta.auth,
		response = action.response;

	// Check for auth error and skip api call incase of auth error;
	if (auth && auth.error && !auth.token) {
		next(action);
		return;
	}

	const ONLINE_DB = 'ONLINE_DB',
		OFFLINE_DB = 'OFFLINE_DB';

	const dbPromise = openDB('canvas-idb', 1, {
		upgrade(db) {
			db.createObjectStore(ONLINE_DB);
			db.createObjectStore(OFFLINE_DB);
		}
	});

	const idbKeyval = {
		async get(key) {
			return (await dbPromise).get(ONLINE_DB, key);
		},
		async set(key, val) {
			return (await dbPromise).put(ONLINE_DB, val, key);
		},
		async getOffline(key) {
			return (await dbPromise).get(OFFLINE_DB, key);
		},
		async setOffline(key, val) {
			return (await dbPromise).put(OFFLINE_DB, val, key);
		},
		async delete(key) {
			return (await dbPromise).delete(ONLINE_DB, key);
		},
		async clear() {
			return (await dbPromise).clear(ONLINE_DB);
		},
		async keys() {
			return (await dbPromise).getAllKeys(ONLINE_DB);
		}
	};

	const appOnlineState = store.getState().apponlinestate;

	switch (method) {
		case 'GET':
			if (action.isOnline) {
				if (action.meta.worker) {
					let _len = action.response.length;
					for (var i = 0; i < _len; i++) {
						idbKeyval.set(action.response[i].url, action.response[i].response);
					}
				} else {
					idbKeyval.set(url, response);
				}
				console.log('[GET]: Online - Saving response to indexedDB', response);
				return next(action);
			} else {
				if (action.meta.isDocListUrl) {
					let queryStringParams = qs.parse(url.split('?')[1].toLowerCase());
					let taskId = queryStringParams.filteredentityidguid;
					idbKeyval.keys().then((d) => {
						let docListUrl = d.filter((x) => {
							let onlineKeyParams = x.indexOf('/documents?') > -1 ? qs.parse(x.split('?')[1].toLowerCase()) : [];
							return (
								onlineKeyParams[documentGetAllParamNames.filteredEntityType.toLowerCase()] === 'task' &&
								onlineKeyParams[documentGetAllParamNames.documentCategoryName.toLowerCase()] === 'evidence' &&
								onlineKeyParams[documentGetAllParamNames.filteredEntityIdGuid.toLowerCase()] === taskId
							);
						})[0];

						idbKeyval
							.get(docListUrl)
							.then((d) => {
								action.response = d || {};
								console.log('[GET]: Offline - working on offline data', d);
								action.mergeOffline = true;
								return next(action);
							})
							.catch((er) => {
								console.info('[GET]: offline - Error in indexedDB call', er, ' - action.type: ', action.type);
								action.error = er;
								action.response = {};
								return next({type: 'API_ERRORS', error: er});
							});
					});
				} else {
					idbKeyval
						.get(url)
						.then((d) => {
							action.response = d || {};
							console.log('[GET]: Offline - working on offline data', d);
							action.mergeOffline = true;
							return next(action);
						})
						.catch((er) => {
							console.info('[GET]: offline - Error in indexedDB call', er, ' - action.type: ', action.type);
							action.error = er;
							action.response = {};
							return next({type: 'API_ERRORS', error: er});
						});
				}
			}
			break;
		case 'POST':
			if (!appOnlineState) {
				let offlineRecords = syncConfig[action.type];
				if (offlineRecords && offlineRecords.length > 0) {
					//append the id in urls for calculating hash
					let filterEntity =
						offlineRecords[0].filterEntity && action?.meta?.api?.data
							? action.meta.api.data[offlineRecords[0].filterEntity]
							: null;
					if (filterEntity) {
						url = url + '_' + filterEntity;
					}
				}

				console.log('url: ', url, 'METHOD: ', method);
				console.log('Stirng hash of URL+Method+Path', stringHash(url.toLowerCase() + method));

				let _id = stringHash(url.toLowerCase() + method);

				idbKeyval.getOffline(_id).then((d) => {
					if (d) {
						idbKeyval.setOffline(_id, [...d, action]);
					} else {
						idbKeyval.setOffline(_id, [action]);
					}
				});

				// Adding the new key _id to the sync_keys record in offline_db
				// This is added as a workaround for Edge browser: Unsupported getAll indexedDB API.
				idbKeyval.getOffline('sync_keys').then((ks) => {
					let syncKeys = ks || [];
					idbKeyval.setOffline('sync_keys', _.union(syncKeys, [_id]));
				});

				console.log('[POST/PATCH]: offline - Saving data to offline IndexedDB', data);
				action.response = data;
				return next(action);
			} else {
				console.log('[POST/PATCH]: Online - update the response to appropriate collection in IndexedDB');
				return next(action);
			}
			break;
		case 'PATCH':
		case 'PUT':
			if (!appOnlineState) {
				let _id = stringHash(url.toLowerCase() + method);
				if (_.isArray(data)) {
					console.log('url: ', url, 'METHOD: ', method, 'PathName: ', data[0].path);
					console.log('Stirng hash of URL+Method+Path', stringHash(url + method + data[0].path));

					_id = stringHash(url.toLowerCase() + method + data[0].path);
				} else {
					let offlineRecords = syncConfig[action.type];
					if (offlineRecords && offlineRecords.length > 0) {
						let patchConfig = offlineRecords.filter((x) => x.updateEntity === Object.keys(data)[0]);
						let updateEntity = patchConfig[0] ? patchConfig[0].updateEntity : '';
						console.log('url: ', url, 'METHOD: ', method, 'PathName: ', updateEntity);
						console.log('Stirng hash of URL+Method+Path', stringHash(url + method + updateEntity));

						_id = stringHash(url.toLowerCase() + method + updateEntity);
					}
				}

				idbKeyval.setOffline(_id, action).then((d) => {
					// Adding the new key _id to the sync_keys record in offline_db
					// This is added as a workaround for Edge browser: Unsupported getAll indexedDB API.
					idbKeyval.getOffline('sync_keys').then((ks) => {
						let syncKeys = ks || [];
						idbKeyval.setOffline('sync_keys', _.union(syncKeys, [_id]));
					});
				});

				console.log('[POST/PATCH]: offline - Saving data to offline IndexedDB', data);
				action.response = data;
				return next(action);
			} else {
				console.log('[POST/PATCH]: Online - update the response to appropriate collection in IndexedDB');
				return next(action);
			}
			break;
		// case 'DELETE':
		// 	if (!appOnlineState) {
		// 		console.log('url: ', url, 'METHOD: ', method);
		// 		console.log('Stirng hash of URL+Method+Path', stringHash(url + method));

		// 		let _id = stringHash(url.toLowerCase() + method);
		// 		let _val = {
		// 			url,
		// 			method,
		// 			action,
		// 			payload: data
		// 		};

		// 		idbKeyval.setOffline(_id, action);
		// 		console.log(
		// 			'[POST/PATCH]: offline - Saving data to offline IndexedDB',
		// 			data
		// 		);
		// 		action.response = data;
		// 		return next(action);
		// 	} else {
		// 		console.log(
		// 			'[POST/PATCH]: Online - update the response to appropriate collection in IndexedDB'
		// 		);
		// 		return next(action);
		// 	}
		// 	break;
		default:
			return next(action);
			break;
	}
};

export default idb_middleware;
